import { DateTime } from "luxon";
import {
  getBookingStatusInfo,
  getOutcomeStatusInfo,
} from "@/domains/appointment/appointments/shared/utils/appointment-status-style";
import { AuditFieldChange, FormattedFieldChange } from "../types/audit-types";

// Private helpers

function getDisplayName(field: string, prismaType?: string, relatedModel?: string): string {
  if (relatedModel) {
    return relatedModel;
  }

  if (field === "bookingStatus" && prismaType === "AppointmentBookingStatus") {
    return "Booking Status";
  }

  if (field === "outcome" && prismaType === "AppointmentOutcome") {
    return "Outcome";
  }

  if (prismaType && (prismaType.includes("Status") || prismaType.includes("Outcome"))) {
    return prismaType
      .replace(/([A-Z])/g, " $1")
      .replace(/^Appointment /, "")
      .trim();
  }

  return field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getFieldIcon(
  field: string,
  prismaType?: string,
  fieldKind?: string,
  isRelation?: boolean,
  _oldValue?: string,
  newValue?: string
): any {
  if (field === "bookingStatus" && prismaType === "AppointmentBookingStatus") {
    if (newValue) {
      const statusInfo = getBookingStatusInfo(newValue as any);
      return {
        type: "react-component",
        component: statusInfo.icon,
        className: statusInfo.iconColor,
      };
    }
    return "📅";
  }

  if (field === "outcome" && prismaType === "AppointmentOutcome") {
    if (newValue) {
      const outcomeInfo = getOutcomeStatusInfo(newValue as any);
      return {
        type: "react-component",
        component: outcomeInfo.icon,
        className: outcomeInfo.iconColor,
      };
    }
    return "✅";
  }

  if (isRelation || fieldKind === "object") {
    switch (prismaType) {
      case "Worker":
        return "👤";
      case "Customer":
        return "🧑‍💼";
      case "Service":
        return "💼";
      case "Salon":
        return "🏢";
      default:
        return "🔗";
    }
  }

  if (fieldKind === "scalar") {
    switch (prismaType) {
      case "DateTime":
        return "📅";
      case "Int":
      case "Float":
      case "Decimal":
        return "🔢";
      case "Boolean":
        return "✅";
      default:
        return "📝";
    }
  }

  if (fieldKind === "enum") {
    return "🏷️";
  }

  if (field.toLowerCase().includes("price") || field.toLowerCase().includes("cost")) {
    return "💰";
  }

  return "📝";
}

function formatEnumValue(value: string): string {
  return value
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
}

function formatSingleValue(
  value: string | null | undefined,
  prismaType?: string,
  fieldKind?: string,
  isRelation?: boolean,
  field?: string
): string {
  if (value === null || value === undefined) {
    return "—";
  }

  if (isRelation || fieldKind === "object") {
    try {
      const obj: any = JSON.parse(value);
      if (prismaType === "Worker") {
        return getWorkerDisplayName(obj);
      }
      if (prismaType === "Customer") {
        return getCustomerDisplayName(obj);
      }
      if (prismaType === "Service") {
        return obj.serviceName || obj.name || obj.title || obj.id;
      }
      return obj.name || obj.title || obj.id || "Unknown";
    } catch {
      return value;
    }
  }

  if (fieldKind === "enum") {
    return formatEnumValue(value);
  }

  if (prismaType === "DateTime") {
    try {
      const dt = DateTime.fromISO(value);
      return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_SHORT) : value;
    } catch {
      return value;
    }
  }

  if (prismaType === "Boolean") {
    return value === "true" ? "Yes" : "No";
  }

  if (field?.toLowerCase().includes("price") || field?.toLowerCase().includes("cost")) {
    const numValue = Number.parseFloat(value);
    if (!Number.isNaN(numValue)) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(numValue / 100);
    }
  }

  return value;
}

function parseWorkerObject(value: string | null | undefined): any {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getWorkerDisplayName(worker: any): string {
  if (!worker) return "Unknown Worker";

  if (worker.user?.firstName && worker.user?.lastName) {
    return `${worker.user.firstName} ${worker.user.lastName}`;
  }

  if (worker.user?.fullName) {
    return worker.user.fullName;
  }

  if (worker.firstName && worker.lastName) {
    return `${worker.firstName} ${worker.lastName}`;
  }

  if (worker.user?.email) {
    return worker.user.email.split("@")[0];
  }

  return worker.id || "Unknown Worker";
}

function getCustomerDisplayName(customer: any): string {
  if (!customer) return "Unknown Customer";

  if (customer.firstName && customer.lastName) {
    return `${customer.firstName} ${customer.lastName}`;
  }

  if (customer.name) {
    return customer.name;
  }

  if (customer.email) {
    return customer.email.split("@")[0];
  }

  return customer.id || "Unknown Customer";
}

function createColorIndicator(color?: string): {
  type: string;
  color: string;
  size: string;
  className: string;
  style: { backgroundColor: string };
} | null {
  if (!color) return null;

  return {
    type: "color-circle",
    color,
    size: "12px",
    className: "inline-block w-3 h-3 rounded-full border border-gray-200 dark:border-gray-600",
    style: {
      backgroundColor: color,
    },
  };
}

function generateChangeDescription(
  displayName: string,
  oldValue: string,
  newValue: string,
  _isRelation?: boolean
): string {
  return `${displayName}: ${oldValue} → ${newValue}`;
}

function formatValues(
  oldValue: string | null | undefined,
  newValue: string | null | undefined,
  prismaType?: string,
  fieldKind?: string,
  isRelation?: boolean,
  field?: string,
  _enumValues?: string[]
) {
  let formattedOldValue = formatSingleValue(oldValue, prismaType, fieldKind, isRelation, field);
  let formattedNewValue = formatSingleValue(newValue, prismaType, fieldKind, isRelation, field);
  let oldColorIndicator: {
    type: string;
    component?: any;
    color?: string;
    size?: string;
    className?: string;
    style?: { backgroundColor: string };
  } | null = null;
  let newColorIndicator: {
    type: string;
    component?: any;
    color?: string;
    size?: string;
    className?: string;
    style?: { backgroundColor: string };
  } | null = null;

  if (isRelation && prismaType === "Worker") {
    const oldWorker = parseWorkerObject(oldValue);
    const newWorker = parseWorkerObject(newValue);

    if (oldWorker) {
      formattedOldValue = getWorkerDisplayName(oldWorker);
      oldColorIndicator = createColorIndicator(oldWorker.color);
    }

    if (newWorker) {
      formattedNewValue = getWorkerDisplayName(newWorker);
      newColorIndicator = createColorIndicator(newWorker.color);
    }
  }

  if (field === "bookingStatus" && prismaType === "AppointmentBookingStatus") {
    if (oldValue) {
      const oldStatusInfo = getBookingStatusInfo(oldValue as any);
      oldColorIndicator = {
        type: "status-icon",
        component: oldStatusInfo.icon,
        className: oldStatusInfo.iconColor,
      };
    }

    if (newValue) {
      const newStatusInfo = getBookingStatusInfo(newValue as any);
      newColorIndicator = {
        type: "status-icon",
        component: newStatusInfo.icon,
        className: newStatusInfo.iconColor,
      };
    }
  }

  if (field === "outcome" && prismaType === "AppointmentOutcome") {
    if (oldValue) {
      const oldOutcomeInfo = getOutcomeStatusInfo(oldValue as any);
      oldColorIndicator = {
        type: "status-icon",
        component: oldOutcomeInfo.icon,
        className: oldOutcomeInfo.iconColor,
      };
    }

    if (newValue) {
      const newOutcomeInfo = getOutcomeStatusInfo(newValue as any);
      newColorIndicator = {
        type: "status-icon",
        component: newOutcomeInfo.icon,
        className: newOutcomeInfo.iconColor,
      };
    }
  }

  return {
    formattedOldValue,
    formattedNewValue,
    oldColorIndicator,
    newColorIndicator,
  };
}

/**
 * Modern field formatter that uses Prisma metadata for intelligent formatting
 */
export const PrismaFieldFormatter = {
  formatFieldChange(change: AuditFieldChange): FormattedFieldChange | null {
    const {
      field,
      oldValue,
      newValue,
      prismaType,
      fieldKind,
      isRelation,
      relatedModel,
      enumValues,
    } = change;

    if (field.endsWith("Id")) {
      return null;
    }

    const displayName = getDisplayName(field, prismaType, relatedModel);

    const { formattedOldValue, formattedNewValue, oldColorIndicator, newColorIndicator } =
      formatValues(oldValue, newValue, prismaType, fieldKind, isRelation, field, enumValues);

    const icon = getFieldIcon(
      field,
      prismaType,
      fieldKind,
      isRelation,
      oldValue ?? undefined,
      newValue ?? undefined
    );

    const changeDescription = generateChangeDescription(
      displayName,
      formattedOldValue,
      formattedNewValue,
      isRelation
    );

    return {
      field,
      displayName,
      formattedOldValue,
      formattedNewValue,
      icon: typeof icon === "string" ? icon : "📝",
      changeDescription,
      oldColorIndicator,
      newColorIndicator,
      customIcon: typeof icon === "object" ? icon : undefined,
    };
  },

  getCustomIcon(field: string, value: string, prismaType?: string): any {
    if (field === "bookingStatus" && prismaType === "AppointmentBookingStatus") {
      const statusInfo = getBookingStatusInfo(value as any);
      return statusInfo.icon;
    }

    if (field === "outcome" && prismaType === "AppointmentOutcome") {
      const outcomeInfo = getOutcomeStatusInfo(value as any);
      return outcomeInfo.icon;
    }

    return null;
  },

  shouldExcludeField(field: string): boolean {
    if (field.endsWith("Id")) return true;
    if (["createdAt", "updatedAt"].includes(field)) return true;
    return false;
  },

  getIdFields(changes: AuditFieldChange[]): FormattedFieldChange[] {
    return changes
      .filter((change) => change.field.endsWith("Id"))
      .map((change) => PrismaFieldFormatter.formatFieldChange(change))
      .filter(Boolean) as FormattedFieldChange[];
  },
};
